import { useState } from "react";
import { Printer, Eye, FileText, Receipt, Pill, TestTube2, ListChecks, ClipboardPen } from "lucide-react";
import { Button } from "@/components/ui/button";
import SelectiveBillDialog from "@/components/SelectiveBillDialog";

/**
 * PrintBundle — tick which OPD documents to print / preview, then fire them
 * as a single batch. Each ticked slip opens in its own tab; print tabs auto-
 * trigger window.print(), preview tabs omit it via the ?preview=1 flag which
 * PrescriptionPrint / BillPrint honour.
 *
 * Docs supported:
 *   rx        → /prescription/:opdId/print          (full Rx incl. investigations)
 *   bill_a4   → /bill/opd/:opdId/print?layout=a4
 *   bill_58   → /bill/opd/:opdId/print?layout=thermal58
 *   bill_80   → /bill/opd/:opdId/print?layout=thermal80
 *   inv_only  → /prescription/:opdId/print?investigation_only=1  (Rx filtered to
 *                just the investigation section — printed on the same letterhead)
 */

const DOCS = [
  { key: "rx",       label: "Prescription (Rx)",       icon: Pill,     href: (id, preview) => `/prescription/${id}/print${preview ? "?preview=1" : ""}` },
  { key: "case",     label: "Case Sheet (blank Rx · no signature)", icon: ClipboardPen, href: (id, preview) => `/prescription/${id}/print?case=1${preview ? "&preview=1" : ""}` },
  { key: "inv_only", label: "Investigation Slip",      icon: TestTube2,href: (id, preview) => `/prescription/${id}/print?investigation_only=1${preview ? "&preview=1" : ""}` },
  { key: "bill_a4",  label: "OPD Bill (A4)",            icon: FileText, href: (id, preview) => `/bill/opd/${id}/print?layout=a4${preview ? "&preview=1" : ""}` },
  { key: "bill_58",  label: "OPD Bill (58 mm Thermal)", icon: Receipt,  href: (id, preview) => `/bill/opd/${id}/print?layout=thermal58${preview ? "&preview=1" : ""}` },
  { key: "bill_80",  label: "OPD Bill (80 mm Thermal)", icon: Receipt,  href: (id, preview) => `/bill/opd/${id}/print?layout=thermal80${preview ? "&preview=1" : ""}` },
];

export default function PrintBundle({ opdId, defaultSelection = ["rx", "bill_a4"], title = "Quick Print Bundle", subtitle = "Tick documents and hit Print or Preview." }) {
  const [sel, setSel] = useState(new Set(defaultSelection));
  const [customOpen, setCustomOpen] = useState(false);

  const toggle = (k) => {
    const next = new Set(sel);
    next.has(k) ? next.delete(k) : next.add(k);
    setSel(next);
  };
  const setAll = (on) => setSel(on ? new Set(DOCS.map((d) => d.key)) : new Set());

  const fire = (preview) => {
    if (!opdId || sel.size === 0) return;
    const chosen = DOCS.filter((d) => sel.has(d.key));
    chosen.forEach((doc, i) => {
      // Stagger opens slightly so browsers don't merge them or block popups
      setTimeout(() => window.open(doc.href(opdId, preview), "_blank"), i * 200);
    });
  };

  return (
    <div className="bg-white border border-blue-200 rounded-sm p-3" data-testid="print-bundle">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="label-tiny">{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
        <div className="text-[11px]">
          <button type="button" onClick={() => setAll(true)} className="text-blue-700 hover:underline mr-2" data-testid="pb-select-all">All</button>
          <button type="button" onClick={() => setAll(false)} className="text-slate-500 hover:underline" data-testid="pb-select-none">None</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {DOCS.map((d) => {
          const Icon = d.icon;
          const on = sel.has(d.key);
          return (
            <label key={d.key} data-testid={`pb-opt-${d.key}`} className={`flex items-center gap-2 text-xs border rounded-sm px-2 py-1.5 cursor-pointer transition ${on ? "border-blue-600 bg-blue-50" : "border-slate-300 hover:border-blue-400"}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(d.key)} className="accent-blue-700" />
              <Icon className={`w-3.5 h-3.5 ${on ? "text-blue-700" : "text-slate-500"}`} />
              <span className={on ? "text-blue-800 font-medium" : "text-slate-700"}>{d.label}</span>
            </label>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        <Button size="sm" variant="outline" disabled={sel.size === 0} onClick={() => fire(true)} className="rounded-sm flex-1" data-testid="pb-preview">
          <Eye className="w-3.5 h-3.5 mr-1"/> Preview ({sel.size})
        </Button>
        <Button size="sm" disabled={sel.size === 0} onClick={() => fire(false)} className="bg-blue-700 hover:bg-blue-800 rounded-sm flex-1" data-testid="pb-print">
          <Printer className="w-3.5 h-3.5 mr-1"/> Print ({sel.size})
        </Button>
      </div>
      <button type="button" onClick={() => setCustomOpen(true)} data-testid="pb-custom-bill"
        className="mt-2 w-full text-[11px] text-blue-700 hover:text-blue-900 hover:underline flex items-center justify-center gap-1">
        <ListChecks className="w-3.5 h-3.5"/> Custom Bill · pick individual items (A4 / 58 / 80)
      </button>
      <SelectiveBillDialog open={customOpen} onOpenChange={setCustomOpen} opdId={opdId}/>
    </div>
  );
}
